import type { FC } from "react";
import { useEffect, useEffectEvent, useState } from "react";

import { NewFilterIcon } from "@jti/brand-icons/new-filter";
import { ReplayIcon } from "@jti/brand-icons/replay";
import { Button, Count, IconButton } from "@jti/core-components";
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTrigger,
} from "@jti/ui/components/Drawer";
import { useDrawer } from "@jti/ui/components/Drawer/providers";

import type { FiltersDrawerProps } from "@commerce/ui";

import styles from "./FiltersDrawer.module.scss";

const DrawerContentInner: FC<FiltersDrawerProps> = ({
  children,
  activeFiltersCount,
  resultsLabel,
  labels,
  onApply,
  onReset,
  onDrawerOpen,
}) => {
  const { isOpen } = useDrawer();
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [openKey, setOpenKey] = useState(0);

  const notifyDrawerOpen = useEffectEvent(() => onDrawerOpen?.());

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setOpenKey((key) => key + 1);
  }

  // Remounts the filter sections on every open so uncontrolled children pick up the freshly synced draft state.
  useEffect(() => {
    if (openKey > 0) notifyDrawerOpen();
    // notifyDrawerOpen is an effect event and must stay out of the deps; react-hooks@5 cannot detect that yet.
    // since we will migrate to react-hooks@6, we can delete this rule exception.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  return (
    <>
      <div className={styles.triggerWrapper}>
        {resultsLabel && (
          <span className={styles.resultsLabel} data-testid="filters-drawer-results-label">
            {resultsLabel}
          </span>
        )}
        <div className={styles.trigger}>
          <DrawerTrigger>
            <IconButton data-testid="filters-drawer-trigger" icon={NewFilterIcon} aria-label={labels.title} />
          </DrawerTrigger>
          {activeFiltersCount > 0 && (
            <span className={styles.triggerBadge}>
              <Count value={activeFiltersCount} data-testid="filters-drawer-count" />
            </span>
          )}
        </div>
      </div>

      <DrawerContent className={styles.content} isMobileFullScreen aria-label={labels.title}>
        <DrawerHeader className={styles.header} hasBackButton backButtonLabel={labels.backButtonLabel}>
          <h6 className={styles.title}>{labels.title}</h6>
        </DrawerHeader>

        <DrawerBody className={styles.body}>
          <div className={styles.filtersContentHeader}>
            <span className={styles.filtersSubtitle}>{labels.filterByLabel}</span>
            <button
              type="button"
              data-testid="filters-drawer-reset"
              className={styles.filtersResetButton}
              onClick={onReset}
            >
              <ReplayIcon aria-hidden="true" className={styles.filtersResetIcon} />
              {labels.resetLabel}
            </button>
          </div>
          <div className={styles.filterSections} key={openKey}>
            {children}
          </div>
        </DrawerBody>

        <DrawerFooter>
          <DrawerCloseTrigger>
            <div className={styles.filtersApplyButtonWrapper}>
              <Button size="lg" onClick={onApply}>
                {labels.showResultsLabel}
              </Button>
            </div>
          </DrawerCloseTrigger>
        </DrawerFooter>
      </DrawerContent>
      <DrawerBackdrop />
    </>
  );
};

export const FiltersDrawer: FC<FiltersDrawerProps> = (props) => (
  <DrawerRoot>
    <DrawerContentInner {...props} />
  </DrawerRoot>
);
